import { FunctionsHttpError } from "@supabase/supabase-js";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import type { Enums } from "@how/database";
import { usePeopleInvalidation, usePersonWrite } from "./queries";

/** One of the church's logins, for choosing which to link (church_logins, users.read; register D17). */
export interface ChurchLogin {
  readonly userId: string;
  readonly email: string;
  readonly status: Enums<"membership_status">;
  /** Whether the login is linked to a person already, even one the reader may not see (D1). */
  readonly linked: boolean;
  /** The linked person, only when the reader may see them. */
  readonly personId: string | null;
  readonly personName: string | null;
}

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

/** The church's logins, for owners and administrators (users.read). Nothing is suggested or matched to the person. */
export function useChurchLogins(enabled: boolean) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["church-logins", tenantId],
    enabled: enabled && tenantId !== null,
    queryFn: async (): Promise<ChurchLogin[]> => {
      const { data, error } = await supabase.rpc("church_logins", { p_tenant_id: tenantId ?? "" });
      if (error) throw error;
      return data.map((row) => ({
        userId: row.user_id,
        email: row.email,
        status: row.status,
        linked: row.linked,
        // Null when the linked person is a child the reader may not see; the generated type does not say so.
        personId: (row.person_id as string | null) ?? null,
        personName: (row.person_name as string | null) ?? null,
      }));
    },
  });
}

export function useLinkLogin() {
  const supabase = useSupabase();
  return usePersonWrite((input: { readonly personId: string; readonly userId: string }) =>
    supabase.rpc("link_person_login", { p_person_id: input.personId, p_user_id: input.userId }),
  );
}

export function useUnlinkLogin() {
  const supabase = useSupabase();
  return usePersonWrite((input: { readonly personId: string }) =>
    supabase.rpc("unlink_person_login", { p_person_id: input.personId }),
  );
}

/** The invite-member function's answer: invited, and whether the person was linked to the login too. */
export interface InviteResult {
  readonly personLinked: boolean;
}

/**
 * Invites an address to the member portal for this person, as one action (#136): invite-member checks the person
 * first (so an under-age person is refused before any email goes out), invites the address with the member role,
 * and links the person once the membership exists. Needs users.manage and people.write.
 */
export function useInviteToPortal() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = usePeopleInvalidation();
  return useMutation({
    mutationFn: async (input: {
      readonly personId: string;
      readonly email: string;
    }): Promise<InviteResult> => {
      const result = await supabase.functions.invoke<{ person_linked?: boolean }>("invite-member", {
        body: {
          tenant_id: tenantId,
          email: input.email,
          role_keys: ["member"],
          surface: "member_portal",
          person_id: input.personId,
        },
      });
      // The client types its error loosely; name what it is.
      const error: unknown = result.error;
      if (error === null) return { personLinked: result.data?.person_linked === true };
      // The function answers { error: { code, message } }; read it, so the screen can say what was refused.
      let code = "";
      let message = error instanceof Error ? error.message : "The invitation was not sent.";
      if (error instanceof FunctionsHttpError) {
        const context: unknown = error.context;
        const body =
          context instanceof Response
            ? ((await context.json().catch(() => null)) as {
                error?: { code?: string; message?: string };
              } | null)
            : null;
        code = body?.error?.code ?? "";
        message = body?.error?.message ?? message;
      }
      throw Object.assign(new Error(message), { code });
    },
    onSuccess: async (_result, input) => {
      await invalidate(input.personId);
    },
  });
}

/**
 * What was refused, in the words a church administrator reads (ADR 0045's three refusals, and the invitation's).
 * The age in the refusal comes from the database's own message, which names it.
 */
export function describeLoginError(error: unknown): string {
  const e = (error ?? {}) as { code?: string; message?: string };
  const message = e.message ?? "";
  const age = /under (\d+)/.exec(message)?.[1];
  switch (e.code) {
    case "23514":
    case "under_portal_age":
      return `Someone under ${age ?? "the portal age"} cannot have a portal login. A parent or guardian manages their record instead.`;
    case "person_has_login":
      return "This person already has a portal login. Unlink it first, then invite them again.";
    case "23505":
      return /person already has a portal login/.test(message)
        ? "This person already has a portal login. Unlink it first, then link the other one."
        : "That login is already linked to someone else in this church. Unlink it there first.";
    case "P0002":
      return /login is not a member/.test(message)
        ? "That login is not a member of this church."
        : "This person could not be found. They may have been deleted.";
    case "person_not_found":
      return "This person could not be found. They may have been deleted.";
    case "already_member":
      return "That address already has a login in this church. Choose it under “Link an existing login” instead.";
    case "42501":
    case "forbidden":
      return "You do not have permission to change portal logins.";
    case "invalid_request":
      return "Check the email address.";
    default:
      return "The change was not saved. Try again.";
  }
}
